import { getEntityById, getLabelFromId } from '@core/utils/collection';
import { idToForm, optionalNumber, toFormValue } from '@core/utils/form';
import type { AlbItem, CreateAlbItemData } from '../../../../api/order';
import { AlbSubtype } from '../../../enum/albSubtype';
import { VestmentType } from '../../../enum/vestmentType';
import {
  albDesignSection,
  albLaceOptionMatches,
  getAlbFeatures,
  getAlbLaceOptionLabel,
  getAlbLaceStyleLabel,
  getAlbSubtypeLabel,
} from '../../alb';
import { albSizeFields } from '../../size';
import {
  EMPTY_LINE_ITEM_BASE,
  baseFieldsFromApi,
  baseLineItemPayload,
  formatMaterialLabel,
  formatOrderItemSizeParts,
  segment,
  segments,
  withSegmentColor,
  withSegmentHex,
} from '../helpers';
import type { AlbItemFormValues, OrderItemCategoryConfig } from '../types';

export const albConfig: OrderItemCategoryConfig = {
  label: 'Alb',
  emptyFormValues: (): AlbItemFormValues => ({
    ...EMPTY_LINE_ITEM_BASE,
    subtype: '',
    length: '',
    shoulder: '',
    sleeve: '',
    neck: '',
    chest: '',
    waist: '',
    figure: '',
    bodyFabric: '',
    bodyColor: '',
    thread: '',
    laceStyle: '',
    laceOption: '',
    withLace: false,
    collar: '',
  }),
  apiToFormValues: (item): AlbItemFormValues => {
    const alb = item as AlbItem;
    return {
      ...baseFieldsFromApi(alb),
      subtype: alb.subtype ?? '',
      length: toFormValue(alb.length),
      shoulder: toFormValue(alb.shoulder),
      sleeve: toFormValue(alb.sleeve),
      neck: toFormValue(alb.neck),
      chest: toFormValue(alb.chest),
      waist: toFormValue(alb.waist),
      figure: toFormValue(alb.figure),
      bodyFabric: idToForm(alb.bodyFabric),
      bodyColor: idToForm(alb.bodyColor),
      thread: idToForm(alb.thread),
      laceStyle: alb.laceStyle ?? '',
      laceOption: alb.laceOption ?? '',
      withLace: alb.withLace ?? false,
      collar: idToForm(alb.collar),
    };
  },
  formToPayload: (item, vestmentType): CreateAlbItemData => {
    const alb = item as AlbItemFormValues;
    const features = getAlbFeatures(vestmentType);
    const sizeFields = new Set(features?.sizeFields ?? []);
    const payload: CreateAlbItemData = {
      ...baseLineItemPayload(alb),
    };

    if (features?.hasSubtypes) {
      payload.subtype = alb.subtype || null;
    }

    for (const field of [
      'length',
      'shoulder',
      'sleeve',
      'neck',
      'chest',
      'waist',
      'figure',
    ] as const) {
      if (sizeFields.has(field)) {
        payload[field] = optionalNumber(alb[field]);
      }
    }

    payload.bodyFabric = optionalNumber(alb.bodyFabric);
    payload.bodyColor = optionalNumber(alb.bodyColor);
    if (albDesignSection(alb.subtype)?.thread) {
      payload.thread = optionalNumber(alb.thread);
    }
    if (alb.subtype === AlbSubtype.Lace) {
      payload.laceStyle = alb.laceStyle || null;
      if (albLaceOptionMatches(alb.laceStyle, alb.laceOption)) {
        payload.laceOption = alb.laceOption || null;
      }
    }

    if (vestmentType === VestmentType.AlbV1Roman) {
      payload.withLace = alb.withLace;
    }
    if (features?.collar) {
      payload.collar = optionalNumber(alb.collar);
    }

    return payload;
  },
  formatSummarySegments: (item, ctx) => {
    const alb = item as AlbItemFormValues;
    const vestment = getEntityById(ctx.vestments, alb.vestment);
    const features = getAlbFeatures(vestment?.type);
    const designSection = albDesignSection(alb.subtype);
    const thread = designSection?.thread
      ? getEntityById(ctx.threads, alb.thread)
      : null;

    return segments(
      segment(
        'Size',
        ...formatOrderItemSizeParts(albSizeFields(vestment?.type), {
          sizeName: getLabelFromId(ctx.sizes, alb.size),
          size: getEntityById(ctx.sizes, alb.size),
          customer: ctx.customer,
          vestmentType: vestment?.type,
          length: alb.length,
          shoulder: alb.shoulder,
          sleeve: alb.sleeve,
          neck: alb.neck,
          chest: alb.chest,
          waist: alb.waist,
          figure: alb.figure,
        })
      ),
      withSegmentColor(
        segment(
          'Body',
          getLabelFromId(ctx.fabrics, alb.bodyFabric),
          getLabelFromId(ctx.colors, alb.bodyColor),
          vestment?.type === VestmentType.AlbV1Roman
            ? `Lace: ${alb.withLace ? 'Yes' : 'No'}`
            : null
        ),
        ctx.colors,
        alb.bodyColor
      ),
      withSegmentHex(
        features?.hasSubtypes
          ? segment(
              'Design',
              getAlbSubtypeLabel(alb.subtype),
              designSection?.design ? alb.designCode : null,
              thread ? formatMaterialLabel(thread) : null,
              ...(alb.subtype === AlbSubtype.Lace
                ? [
                    getAlbLaceStyleLabel(alb.laceStyle),
                    getAlbLaceOptionLabel(alb.laceOption),
                  ]
                : [])
            )
          : null,
        thread?.hexCode
      ),
      features?.collar
        ? segment('Collar', getLabelFromId(ctx.collars, alb.collar))
        : null
    );
  },
};
